/* ──────────────────────────────────────────────────────────────────────────
   Downloads, the parts the popup and the server both need: what a folder
   line can be set to, a Drive link to its folder id, how far back "Download
   previous posts?" reaches, and what a downloaded file is called.
   ────────────────────────────────────────────────────────────────────────── */

export const KINDS = [
  { id: "all", label: "All" },
  { id: "photos", label: "Photos" },
  { id: "videos", label: "Videos" },
] as const;

export type Kind = (typeof KINDS)[number]["id"];

export const UNITS = [
  { id: "days", label: "days", days: 1 },
  { id: "weeks", label: "weeks", days: 7 },
  { id: "months", label: "months", days: 30 },
] as const;

export type Unit = (typeof UNITS)[number]["id"];

/** "Download previous posts?": none, everything on the profile, or the past so many days, weeks or months. */
export type Backfill = { all: true } | { amount: number; unit: Unit };

/** The moment a backfill reaches back to, or null for the whole profile. */
export function backfillSince(backfill: Backfill, now: Date): Date | null {
  if ("all" in backfill) return null;
  const unit = UNITS.find((u) => u.id === backfill.unit) ?? UNITS[0];
  return new Date(now.getTime() - backfill.amount * unit.days * 86_400_000);
}

/**
 * A folder id out of whatever was pasted: a folder link (with /u/0/, ?usp=
 * and the like), an open?id= link, or the bare id. Null when it is none of them.
 */
export function folderIdFrom(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  const path = /\/folders\/([A-Za-z0-9_-]{10,})/.exec(text);
  if (path) return path[1]!;
  const query = /[?&]id=([A-Za-z0-9_-]{10,})/.exec(text);
  if (query && /drive\.google\.com/.test(text)) return query[1]!;
  return /^[A-Za-z0-9_-]{10,}$/.test(text) ? text : null;
}

export const folderUrl = (id: string) => `https://drive.google.com/drive/folders/${id}`;

/** "@handle 2026-10-15 CODE.mp4", a carousel's slides "CODE-1", "CODE-2" (Jay, 2026-10-15). */
export function fileName(handle: string, day: string, code: string, slide: number, ext: string): string {
  return `@${handle} ${day} ${code}${slide ? `-${slide}` : ""}.${ext}`;
}

/** Whether a folder set to `kind` takes a photo or a video. */
export const takes = (kind: Kind, media: "photo" | "video") => kind === "all" || (kind === "photos" ? media === "photo" : media === "video");
